import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import FileUploadButton from "@/components/admin/FileUploadButton";
import { resolveGoogleLink, GOOGLE_HINT } from "@/components/admin/googleLink";

const EMPTY = { title: "", description: "", image_url: "", order: 0, bookable: true };
const inputClass = "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3 text-sm focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-bold uppercase text-stone-700";

export default function PrestationsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/prestations").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0, bookable: form.bookable !== false };
      if (editingId) {
        await api.put(`/admin/prestations/${editingId}`, payload);
        toast.success("Prestation mise à jour");
      } else {
        await api.post("/admin/prestations", payload);
        toast.success("Prestation ajoutée");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({ title: item.title, description: item.description, image_url: item.image_url || "", order: item.order, bookable: item.bookable !== false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette prestation ?")) return;
    await api.delete(`/admin/prestations/${id}`).catch(() => {});
    toast.success("Prestation supprimée");
    load();
  };

  return (
    <div data-testid="prestations-manager" className="grid grid-cols-1 gap-10 lg:grid-cols-2">
      <form onSubmit={submit} className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">
          {editingId ? "Modifier la prestation" : "Ajouter une prestation"}
        </h2>
        <div>
          <label className={labelClass}>Titre *</label>
          <input data-testid="prestation-title-input" className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div>
          <label className={labelClass}>Description *</label>
          <textarea data-testid="prestation-description-input" rows="4" className={inputClass} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
        </div>
        <div>
          <label className={labelClass}>Image (facultatif)</label>
          <input data-testid="prestation-image-input" className={inputClass} placeholder="https://..." value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} onBlur={(e) => resolveGoogleLink(e.target.value, (u) => setForm((f) => ({ ...f, image_url: u })))} />
          <p className="mt-1 text-xs text-stone-500">{GOOGLE_HINT}</p>
          <div className="mt-2">
            <FileUploadButton testId="prestation-image-upload" accept="image/*" label="Ou téléverser une image" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} />
          </div>
        </div>
        <div>
          <label className={labelClass}>Ordre d'affichage (1 = en premier)</label>
          <input data-testid="prestation-order-input" type="number" className={inputClass} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input data-testid="prestation-bookable-checkbox" type="checkbox" checked={form.bookable !== false} onChange={(e) => setForm({ ...form, bookable: e.target.checked })} className="h-4 w-4 accent-[#B83227]" />
          Réservable en ligne (affiche le bouton « Demander cette prestation »). Décochez pour les prestations non réservables, comme les stages organisés par le groupe.
        </label>
        <div className="flex gap-3">
          <button type="submit" data-testid="prestation-save-button" disabled={saving} className="rounded bg-[#B83227] px-6 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#96281E] disabled:opacity-60">
            {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Ajouter"}
          </button>
          {editingId && (
            <button type="button" data-testid="prestation-cancel-button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded border border-stone-300 px-6 py-2.5 text-xs font-bold uppercase text-stone-600">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">Prestations ({items.length})</h2>
        {items.map((p) => (
          <div key={p.id} data-testid={`prestation-item-${p.id}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#1E1B18]">{p.order}. {p.title}</p>
                <p className="mt-1 line-clamp-2 text-xs text-stone-500">{p.description}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(p)} data-testid={`prestation-edit-${p.id}`} className="rounded border border-stone-300 p-2 text-stone-600 hover:border-stone-500" aria-label="Modifier">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => remove(p.id)} data-testid={`prestation-delete-${p.id}`} className="rounded bg-red-50 p-2 text-red-700 hover:bg-red-100" aria-label="Supprimer">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
